import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { getRequest } from '../../apis/ApiInstence';
import { Calendar, User, ArrowLeft, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import headerBg from '../../Assets/img/parshvanath-temple-khajuraho.jpg';

const BlogDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const assetBaseUrl = useMemo(() => {
    let base = process.env.REACT_APP_BACKEND || '';
    base = base.replace('/khajuraho/', '/');
    if (!base.endsWith('/')) {
      base = `${base}/`;
    }
    return base;
  }, []);

  const formatDate = (value) => {
    if (!value) return 'Recent';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recent';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const formatContent = (content) => {
    if (!content) return '';
    let html = content;
    // Replace <p><b>...</b></p> with <h2>...</h2>
    html = html.replace(/<p>\s*<(strong|b)>(.*?)<\/\1>\s*<\/p>/gi, '<h2>$2</h2>');
    // Replace <div><b>...</b></div> with <h2>...</h2>
    html = html.replace(/<div>\s*<(strong|b)>(.*?)<\/\1>\s*<\/div>/gi, '<h2>$2</h2>');
    // Replace standalone <b>...</b> with <h2>...</h2>
    html = html.replace(/(?:<br\s*\/?>|\n|^)\s*<(strong|b)>(.*?)<\/\1>\s*(?:<br\s*\/?>|\n|$)/gi, '<h2>$2</h2>');

    // Backend sends plain text separated by blank lines. Split into blocks:
    // short standalone lines -> <h2>, normal text -> <p>,
    // short phrases after a ":" line -> <ul><li> list.
    // This removes the giant gaps caused by raw newlines.
    const blocks = html.split(/\n\s*\n/);
    const out = [];
    let listBuffer = [];
    const flushList = () => {
      if (listBuffer.length > 0) {
        out.push(`<ul>${listBuffer.map((li) => `<li>${li}</li>`).join('')}</ul>`);
        listBuffer = [];
      }
    };

    blocks.forEach((block) => {
      const trimmed = block.trim();
      if (!trimmed) return;
      // Already HTML -> keep as-is (flush any open list first)
      if (trimmed.startsWith('<')) {
        flushList();
        out.push(trimmed);
        return;
      }
      const singleLine = !trimmed.includes('\n');
      const text = trimmed.replace(/\s*\n\s*/g, ' ');
      // Short phrase continuing a list started by a ":" line
      if (listBuffer.length > 0 && singleLine && text.length < 80) {
        listBuffer.push(text);
        return;
      }
      flushList();
      // A line ending with ":" introduces a list -> keep as paragraph
      if (singleLine && text.endsWith(':')) {
        out.push(`<p>${text}</p>`);
        return;
      }
      // Short standalone heading line -> <h2> (questions ending with ? stay headings)
      if (singleLine && text.length > 0 && text.length < 100 && !/[.:]$/.test(text)) {
        // If previous output was a ":" paragraph and this is short, treat as list item
        const prev = out.length > 0 ? out[out.length - 1] : '';
        if (/<p>.*:<\/p>$/.test(prev)) {
          listBuffer.push(text);
          return;
        }
        out.push(`<h3>${text}</h3>`);
        return;
      }
      out.push(`<p>${text}</p>`);
    });
    flushList();

    return out.join('\n');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const response = await getRequest(`blogs/${id}`);
        const blogData = response?.data?.data || response?.data;
        if (blogData) {
          let parsedImages = [];
          if (Array.isArray(blogData.images)) {
            parsedImages = blogData.images;
          } else if (typeof blogData.images === 'string' && blogData.images.trim().length > 0) {
            try {
              const maybeArray = JSON.parse(blogData.images);
              parsedImages = Array.isArray(maybeArray) ? maybeArray : [blogData.images];
            } catch (e) {
              parsedImages = [blogData.images];
            }
          }
          const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
          const image = firstImage
            ? (firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`)
            : 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop';
          
          setBlog({
            ...blogData,
            image,
            title: blogData.heading || 'Untitled',
            date: formatDate(blogData.createdAt),
            author: blogData.author || 'Khajuraho Routes Team',
          });
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, assetBaseUrl, i18n.language]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">{t('blogdetail_loading')}</div>;
  }

  if (!blog) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">{t('blogdetail_not_found')}</div>;
  }

  return (
    <main className="w-full bg-[#f4f5f7] text-gray-900 overflow-hidden font-sans pb-24">
      <Helmet>
        <title>{`${blog.title} | Khajuraho Routes`}</title>
        <meta name="description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta name="keywords" content={`${blog.title}, Khajuraho travel blog, Khajuraho news`} />
        <link rel="canonical" href={`https://khajurahoroutes.com/news-media/${id}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://khajurahoroutes.com/news-media/${id}`} />
        <meta property="og:title" content={`${blog.title} | Khajuraho Routes`} />
        <meta property="og:description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta property="og:image" content={blog.image} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blog.title} | Khajuraho Routes`} />
        <meta name="twitter:description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta name="twitter:image" content={blog.image} />

        {/* JSON-LD Blog Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": "${blog.title.replace(/"/g, '\\"')}",
              "image": "${blog.image}",
              "author": {
                "@type": "Person",
                "name": "${blog.author || 'Khajuraho Routes'}"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Khajuraho Routes",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://khajurahoroutes.com/android-chrome-512x512.png"
                }
              },
              "datePublished": "${blog.date}",
              "description": "${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').replace(/"/g, '\\"').substring(0, 200) : ''}"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative w-full pt-8 pb-8 sm:pt-12 sm:pb-12 overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
          {/* Hero Image Background */}
          {/* <img 
            src={headerBg} 
            className="w-full h-full object-cover opacity-70" 
            alt="Khajuraho travel blog - temples and Central India stories" 
            decoding="async"
          /> */}
          <div className="absolute inset-0 bg-gray-900/60" />
          {/* <div className="absolute inset-0 bg-gradient-to-t from-[#f4f5f7] via-transparent to-black/50" /> */}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/news-media" className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white mb-6 transition-colors font-medium bg-white/10 px-5 py-2 rounded-full backdrop-blur-md border border-white/20 hover:bg-white/20">
              <ArrowLeft size={14} /> {t('blogdetail_back')}
            </Link>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 max-w-7xl"
          >
            {blog.title}
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-200"
          >
            <div className="flex items-center gap-2 px-4 py-2 backdrop-blur-md">
              <Calendar size={16} className="text-orange-400" />
              <span className="font-medium">{blog.date}</span>
            </div>
            <div className="flex items-center gap-2  px-4 py-2  backdrop-blur-md">
              <User size={16} className="text-orange-400" />
              <span className="font-medium">{blog.author}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 mt-5  relative z-20">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className=""
        >
          
          {/* Main image */}
          {blog.image && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mb-8 w-full rounded-2xl overflow-hidden"
            >
               <img 
                 src={blog.image} 
                 alt={blog.title} 
                 loading="lazy"
                 decoding="async"
                 className="w-full h-auto object-containt"
              />
            </motion.div>
          )}

          <div 
            className="prose prose-base sm:prose-lg max-w-none text-gray-700 leading-8 prose-headings:font-bold prose-headings:text-gray-900 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:mb-5 prose-p:mt-0 prose-ul:my-5 prose-li:mb-2 prose-a:text-orange-600 prose-img:rounded-2xl prose-img:w-full"
            dangerouslySetInnerHTML={{ __html: formatContent(blog.content) }}
          />
        </motion.div>
      </section>
    </main>
  );
};

export default BlogDetails;
